import React, { useEffect, useState } from 'react';
import * as go from 'gojs';
import { ReactDiagram } from 'gojs-react';
import { FxmsData } from "../fxms/datas/FxData";

export function Monitoring() {
  const [networkData, setNetworkData] = useState();

  const [lastUpdateTime, setLastUpdateTime] = useState(new Date().toLocaleString('ko-KR'));

  const fetchStatus = async () => {
    const uri = "vup/monitoring/status"

    try {
      const response = await FxmsData.GET(uri);
      const nodeList = response.data.nodes;

      // 노드 상태 직접 체크
      const nodes = await Promise.all(nodeList.map(async node => {
        if (!node.url) {
          return createErrorNode(node);
        }

        try {
          // 프록시 엔드포인트를 통해 상태 체크
          const proxyResponse = await FxmsData.GET(`vup/monitoring/nodeCheck?url=${encodeURIComponent(node.url)}`);
          return {
            key: node.nodeId,
            name: node.nodeName,
            status: proxyResponse.data.status === "1" ? "connected" : "error",
            loc: node.loc,
            responseTime: proxyResponse.data.responseTime,
            url: node.url
          };
        } catch (error) {
          return createErrorNode(node);
        }
      }));

      // 링크 데이터 추가
      const links = response.data.links.map(link => {
        return {
          key: link.linkId,
          from: link.start,
          to: link.finish,
          text: link.linkName,
          status: link.linkInfo?.status === "1" ? "connected" : "error",
          dataCount: link.linkInfo?.totalCount || 0,
          lastCommunication: link.linkInfo?.latestDataTime 
          ? `${String(link.linkInfo.latestDataTime).slice(0,4)}. ${String(link.linkInfo.latestDataTime).slice(4,6)}. ${String(link.linkInfo.latestDataTime).slice(6,8)} ${String(link.linkInfo.latestDataTime).slice(8,10)}:${String(link.linkInfo.latestDataTime).slice(10,12)}:${String(link.linkInfo.latestDataTime).slice(12,14)}`
          : '-'
        };
      });

      setNetworkData({
        nodes: nodes,
        links: links
      });

      // 마지막 업데이트 시간 설정
      setLastUpdateTime(new Date().toLocaleString('ko-KR'));
    } catch (error) {
      console.error('상태 업데이트 실패:', error);
    }
  };

  // 에러 노드 생성 헬퍼 함수
  const createErrorNode = (node) => ({
    key: node.nodeId,
    name: node.nodeName,
    status: "error",
    loc: node.loc,
    responseTime: 0
  });

  // 주기적으로 상태 업데이트 (5분마다)
  useEffect(() => {
    // 최초 실행
    fetchStatus();
    const intervalId = setInterval(fetchStatus, 300000); // 5분마다 갱신
    
    return () => clearInterval(intervalId);
  }, []);

  function initDiagram() {
    const $ = go.GraphObject.make;
    const diagram = $(go.Diagram, {
      'undoManager.isEnabled': true, // 노드 고정
      initialContentAlignment: go.Spot.Center,
      "animationManager.isEnabled": false, // 애니메이션 기능을 비활성화
      layout: $(go.Layout),
      model: $(go.GraphLinksModel, {
        linkKeyProperty: 'key'
      })
    });
    
    diagram.toolManager.panningTool.isEnabled = true;
    diagram.toolManager.draggingTool.isEnabled = true;

    diagram.nodeTemplate =
      $(go.Node, "Auto",
        {
          padding: 15,
          locationSpot: go.Spot.Center,
          selectionAdorned: false,
          shadowOffset: new go.Point(5, 5),
          shadowBlur: 10,
          movable: true,
          cursor: "pointer",
          toolTip: $(go.Adornment, "Auto",
            $(go.Shape, "RoundedRectangle", { fill: "#1E1E1E" }),
            $(go.Panel, "Vertical",
              { margin: 10 },
              $(go.TextBlock, 
                { stroke: "white", margin: 3, font: "12px sans-serif" },
                new go.Binding("text", "", data => `상태: ${data.status === 'connected' ? '정상' : '에러'}`)),
              $(go.TextBlock, 
                { stroke: "white", margin: 3, font: "12px sans-serif" },
                new go.Binding("text", "responseTime", rt => `응답시간: ${rt}ms`))
            )
          )
        },
        new go.Binding("location", "loc", go.Point.parse).makeTwoWay(go.Point.stringify),
        $(go.Panel, "Auto",
          {
            click: function(e, obj) {
              const node = obj.part;
              if (node && node.data) {
                if (node.data.url) {
                  window.open(node.data.url, '_blank', 'noopener,noreferrer');
                }
              }
              e.handled = true;
            }
          },
          $(go.Shape,
            "RoundedRectangle",
            { 
              fill: "#1E1E1E",
              strokeWidth: 0,
              stroke: null,
              shadowVisible: true,
              width: 300,
              height: 100
            },
            new go.Binding("fill", "status", status => 
              status === 'connected' ? "#2ECC71" : "#E74C3C"
            )
          ),
          $(go.TextBlock,
            { 
              margin: 12,
              font: "bold 18px sans-serif",
              stroke: "white",
              textAlign: "center",
              width: 280
            },
            new go.Binding("text", "name")
          )
        )
      );

    diagram.linkTemplate =
      $(go.Link,
        { 
          routing: go.Link.AvoidsNodes,
          curve: go.Link.JumpOver,
          corner: 30,
          toShortLength: 8,
          toolTip: $(go.Adornment, "Auto",
            $(go.Shape, { fill: "#FFFFDD", stroke: "#CCCCCC" }),
            $(go.Panel, "Vertical",
              { margin: new go.Margin(6, 8, 2, 8) },
              $(go.TextBlock, 
                { 
                  margin: new go.Margin(2, 2, 2, 2),
                  font: "14px sans-serif"
                },
                new go.Binding("text", "", data => {
                  let statusText = data.status === 'connected' ? '정상' : '오류';
                  return `상태: ${statusText}\n` +
                         `수집 데이터: ${data.dataCount} 건\n` +
                         `마지막 통신: ${data.lastCommunication}`;
                })
              )
            )
          )
        },
        $(go.Shape,
          { 
            strokeWidth: 3,
            stroke: "#2E3B4E" 
          },
          new go.Binding("stroke", "", data => {
            if (data.status === 'error') return "#E74C3C";  // 빨간색 (에러)
            return "#2ECC71";  // 초록색 (정상)
          })
        ),
        $(go.Panel, "Auto",
          {
            segmentIndex: NaN,
            segmentFraction: 0.5,
          },
          $(go.Shape,
            {
              fill: "white",
              stroke: null,
              margin: 3
            }
          ),
          $(go.Panel, "Horizontal",
            {
              margin: 5,
              segmentOffset: new go.Point(0, -20)
            },
            $(go.TextBlock,
              {
                font: "14px sans-serif",
                stroke: "#666",
                margin: new go.Margin(0, 4, 0, 0)
              },
              new go.Binding("text", "", data => {
                return `${data.text}`;
              })
            )
          )
        )
      );

    // 다이어그램 설정
    diagram.initialContentAlignment = go.Spot.Center;
    diagram.contentAlignment = go.Spot.Center;
    diagram.scale = 1.1;  // 전체적인 뷰 확대

    return diagram;
  }

  const diagramStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'white'
  };

  return (
    <div style={{ 
      width: '95%',
      height: '95%',
      backgroundColor: '#f5f5f5',
      padding: '20px',
      boxSizing: 'border-box',
      marginLeft: '100px',
      marginTop: '60px'
    }}>
      <div style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* 마지막 업데이트 시간 표시 */}
        {lastUpdateTime && (
          <div style={{
            position: 'absolute',
            top: '100px',
            left: '60px',
            padding: '8px 12px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            fontSize: '23px',
            color: '#666',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            마지막 업데이트: {lastUpdateTime}
          </div>
        )}
        
        {networkData && (
          <ReactDiagram
            initDiagram={initDiagram}
            divClassName='diagram-component'
            nodeDataArray={networkData.nodes}
            linkDataArray={networkData.links}
            style={diagramStyle}
          />
        )}
      </div>

      {/* 재조회 버튼 */}
      <div style={{ 
        position: 'fixed',
        top: '270px',
        left: '1000px',
        zIndex: 1000
      }}>
        <button 
          onClick={fetchStatus}
          style={{
            padding: '10px 20px',
            fontSize: '14px',
            borderRadius: '5px',
            border: 'none',
            backgroundColor: '#3498db',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          재조회
        </button>
      </div>
    </div>
  );
}